import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  ButtonGroupComponent,
  ButtonModule,
  ChipComponent,
  ChipListComponent,
} from '@progress/kendo-angular-buttons';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import {
  EditorBoldButtonDirective,
  EditorComponent,
  EditorItalicButtonDirective,
  EditorModule,
  EditorUnderlineButtonDirective,
} from '@progress/kendo-angular-editor';
import { GridModule } from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule, NumericTextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { AvatarComponent, LayoutModule } from '@progress/kendo-angular-layout';
import { MenusModule } from '@progress/kendo-angular-menu';
// Load the required calendar data for the de locale
import '@progress/kendo-angular-intl/locales/de/calendar';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { PopupModule } from '@progress/kendo-angular-popup';
import { UploadComponent, UploadDropZoneDirective } from '@progress/kendo-angular-upload';
import { AfterValueChangedDirective } from '../core/directives/after-value-changed.directive';
import { CollapsibleDirective } from '../core/directives/collapsible.directive';
import { ShivePopupDirective } from '../core/directives/shive-popup.directive';
import { SortableRowsDirective } from '../core/directives/sortable-rows.directive';
import { MemberNamePipe } from '../core/pipes/member-name.pipe';
import { SafeHtmlPipe } from '../core/pipes/safe-html.pipe';
import { ShiveCurrencyPipe } from '../core/pipes/shive-currency.pipe';
import { EditDocumentSectionBaseDataComponent } from '../pages/finances/invoices/invoice-creation-editing/sidebar/edit-document-section-base-data/edit-document-section-base-data.component';
import { EditDocumentSectionBaseStylingComponent } from '../pages/finances/invoices/invoice-creation-editing/sidebar/edit-document-section-base-styling/edit-document-section-base-styling.component';
import { EditDocumentSectionHandlingComponent } from '../pages/finances/invoices/invoice-creation-editing/sidebar/edit-document-section-handling/edit-document-section-handling.component';
import { EditDocumentSectionImageUploadComponent } from '../pages/finances/invoices/invoice-creation-editing/sidebar/edit-document-section-image-upload/edit-document-section-image-upload.component';
import { EditDocumentSectionWysiwygComponent } from '../pages/finances/invoices/invoice-creation-editing/sidebar/edit-document-section-wysiwyg/edit-document-section-wysiwyg.component';
import { FuelbarComponent } from './components/charts/fuelbar/fuelbar.component';
import { HistoryTimeLineComponent } from './components/charts/history-time-line/history-time-line.component';
import { ContextMenuAnchorComponent } from './components/context-menu/context-menu-anchor/context-menu-anchor.component';
import { ShiveContextMenuComponent } from './components/context-menu/shive-context-menu/shive-context-menu.component';
import { DateAndTimeInputDialogComponent } from './components/dialogs/date-and-time-input-dialog/date-and-time-input-dialog.component';
import { FileUploadDialogComponent } from './components/dialogs/file-upload-dialog/file-upload-dialog.component';
import { PendingChangesDialogComponent } from './components/dialogs/pending-changes-dialog/pending-changes-dialog.component';
import { DraggableFinancialRecordsTableComponent } from './components/document-editing/draggable-financial-records-table/draggable-financial-records-table.component';
import { EditDocumentSectionControlComponent } from './components/document-editing/edit-document-section-control/edit-document-section-control.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { NotificationComponent } from './components/notification/notification.component';
import { PlannerControlBarComponent } from './components/planner-control-bar/planner-control-bar.component';
import { ShiveAvatarComponent } from './components/shive-avatar/shive-avatar.component';
import { ShiveTaglistDialogComponent } from './components/shive-taglist/shive-taglist-dialog/shive-taglist-dialog.component';
import { ShiveTaglistComponent } from './components/shive-taglist/shive-taglist.component';
import { ShiveDropdownlistComponent } from './components/shive-controls/shive-dropdownlist/shive-dropdownlist.component';
import { ShiveGridWrapperComponent } from './components/shive-controls/shive-grid/shive-grid-wrapper.component';

@NgModule({
  declarations: [
    NotFoundComponent,
    NotificationComponent,
    ShiveContextMenuComponent,
    ContextMenuAnchorComponent,
    ShiveDropdownlistComponent,
    ShiveGridWrapperComponent,
    PendingChangesDialogComponent,
    ShiveAvatarComponent,
    HistoryTimeLineComponent,
    FuelbarComponent,
    DateAndTimeInputDialogComponent,
    EditDocumentSectionControlComponent,
    EditDocumentSectionBaseStylingComponent,
    EditDocumentSectionImageUploadComponent,
    EditDocumentSectionWysiwygComponent,
    EditDocumentSectionBaseDataComponent,
    DraggableFinancialRecordsTableComponent,
    EditDocumentSectionHandlingComponent,
    PlannerControlBarComponent,
    FileUploadDialogComponent,
    ShiveTaglistComponent,
    ShiveTaglistDialogComponent,
  ],
  imports: [
    AvatarComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MenusModule,
    PopupModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ChartsModule,
    DropDownsModule,
    LabelModule,
    InputsModule,
    FontAwesomeModule,
    CollapsibleDirective,
    ButtonModule,
    IndicatorsModule,
    DateInputsModule,
    NgOptimizedImage,
    AfterValueChangedDirective,
    ShivePopupDirective,
    ShiveCurrencyPipe,
    SafeHtmlPipe,
    MemberNamePipe,
    ButtonGroupComponent,
    EditorComponent,
    EditorBoldButtonDirective,
    EditorItalicButtonDirective,
    EditorUnderlineButtonDirective,
    EditorModule,
    SortableRowsDirective,
    UploadDropZoneDirective,
    UploadComponent,
    ChipListComponent,
    ChipComponent,
  ],
  exports: [
    NotFoundComponent,
    FormsModule,
    ReactiveFormsModule,
    LayoutModule,
    InputsModule,
    NumericTextBoxModule,
    LabelModule,
    ButtonModule,
    DraggableFinancialRecordsTableComponent,
    DropDownsModule,
    DialogModule,
    NotificationModule,
    DateInputsModule,
    PopupModule,
    ChartsModule,
    GridModule,
    IconsModule,
    ShiveContextMenuComponent,
    ShiveDropdownlistComponent,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NotificationComponent,
    ShiveGridWrapperComponent,
    ShiveAvatarComponent,
    ContextMenuAnchorComponent,
    HistoryTimeLineComponent,
    FontAwesomeModule,
    FuelbarComponent,
    DateAndTimeInputDialogComponent,
    EditDocumentSectionControlComponent,
    EditDocumentSectionBaseStylingComponent,
    EditDocumentSectionImageUploadComponent,
    EditDocumentSectionWysiwygComponent,
    EditDocumentSectionHandlingComponent,
    NgOptimizedImage,
    EditDocumentSectionBaseDataComponent,
    PlannerControlBarComponent,
    ShiveTaglistComponent,
  ],
})
export class SharedModule {}
